"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { Field } from "@base-ui/react/field";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../utils/cn.js";
import { mergeRefs } from "../../utils/mergeRefs.js";
import { getTooltipSideOffset } from "../Tooltip/utils.js";
import { inputTextVariants } from "./InputText.variants.js";
import { InputTextWrapper } from "./InputTextWrapper.js";
const CLEARED_ANNOUNCEMENT = 'Input cleared';
function dispatchNativeClear(input) {
    const valueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    valueSetter?.call(input, '');
    input.dispatchEvent(new Event('input', {
        bubbles: true
    }));
}
function getDisplayErrorText(errorText, errorMessage, hasExternalError, internalError) {
    if (errorText) return errorText;
    if (errorMessage && (hasExternalError || internalError)) return errorMessage;
}
function getClearedAnnouncement(clearedAt) {
    if (0 === clearedAt) return '';
    return clearedAt % 2 === 0 ? `${CLEARED_ANNOUNCEMENT}​` : CLEARED_ANNOUNCEMENT;
}
const InputText_InputText = /*#__PURE__*/ forwardRef(function({ label, isLabelHidden, helperText, errorText, maxLength, iconLeft, iconRight, emphasis, isInset, size, className, isDisabled, errorMessage, validate, 'aria-invalid': ariaInvalid, validationMode = 'onBlur', onChange, hasExternalError = false, rightIconButtonProps, isClearable = false, onClear, clearButtonLabel = 'Clear input', ...props }, ref) {
    const { name, ...rest } = props;
    const inputRef = useRef(null);
    const mergedRef = useMemo(()=>mergeRefs(ref, inputRef), [
        ref
    ]);
    const isClearingRef = useRef(false);
    const [internalError, setInternalError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [clearedAt, setClearedAt] = useState(0);
    const isControlled = void 0 !== props.value;
    const [uncontrolledHasValue, setUncontrolledHasValue] = useState(()=>'' !== String(props.defaultValue ?? ''));
    const hasValue = isControlled ? '' !== String(props.value ?? '') : uncontrolledHasValue;
    const isReadOnly = Boolean(props.readOnly);
    const showClear = isClearable && !isDisabled && !isReadOnly && hasValue;
    const [isHydrated, setIsHydrated] = useState(false);
    useEffect(()=>setIsHydrated(true), []);
    const tooltipSideOffset = getTooltipSideOffset(size);
    const hasPlaceholder = Boolean(props.placeholder);
    const hasErrorTextOverride = Boolean(errorText);
    const hasError = hasErrorTextOverride || hasExternalError || internalError;
    const styles = inputTextVariants({
        emphasis,
        isDisabled,
        hasIconLeft: !!iconLeft,
        hasIconRight: !!iconRight,
        isInset,
        hasPlaceholder,
        size,
        error: hasError,
        success: success && !hasError,
        rightIconButton: !!rightIconButtonProps,
        hasClear: showClear
    });
    function handleChange(event) {
        if (!isControlled) setUncontrolledHasValue('' !== event.target.value);
        onChange?.(event);
    }
    function handleClear() {
        const input = inputRef.current;
        if (!input) return;
        isClearingRef.current = true;
        dispatchNativeClear(input);
        isClearingRef.current = false;
        setInternalError(false);
        setSuccess(false);
        setClearedAt((count)=>count + 1);
        onClear?.();
        input.focus();
    }
    const handleValidation = (value)=>{
        if (!isClearingRef.current && !hasErrorTextOverride && !hasExternalError && validate && 'string' == typeof value) {
            const isValid = validate(value);
            setInternalError(!isValid);
            setSuccess(isValid);
        }
        return null;
    };
    const displayErrorText = getDisplayErrorText(errorText, errorMessage, hasExternalError, internalError);
    const clearedAnnouncement = getClearedAnnouncement(clearedAt);
    return /*#__PURE__*/ jsxs(Field.Root, {
        name: name,
        className: cn(styles.root(), className),
        validate: handleValidation,
        disabled: isDisabled,
        validationMode: validationMode,
        "data-ssr": isHydrated ? void 0 : '',
        children: [
            /*#__PURE__*/ jsx(Field.Control, {
                render: (inputProps)=>/*#__PURE__*/ jsx(InputTextWrapper, {
                        ...rest,
                        id: inputProps.id,
                        ref: mergedRef,
                        styles: styles,
                        iconLeft: iconLeft,
                        ariaInvalid: Boolean(ariaInvalid) || hasError,
                        errorText: displayErrorText,
                        maxLength: maxLength,
                        onChange: handleChange,
                        error: hasError,
                        tooltipSideOffset: tooltipSideOffset,
                        errorMessage: errorMessage,
                        success: success && !hasError,
                        iconRight: iconRight,
                        rightIconButtonProps: rightIconButtonProps,
                        isDisabled: isDisabled,
                        size: size,
                        emphasis: emphasis,
                        showClear: showClear,
                        clearButtonLabel: clearButtonLabel,
                        onClear: handleClear
                    })
            }),
            isClearable ? /*#__PURE__*/ jsx("div", {
                className: "sr-only",
                "aria-live": "polite",
                "aria-atomic": "true",
                children: clearedAnnouncement
            }) : null,
            label ? /*#__PURE__*/ jsx(Field.Label, {
                className: cn(styles.labelSlot(), isLabelHidden ? 'hidden' : ''),
                children: label
            }) : null,
            helperText || displayErrorText || maxLength ? /*#__PURE__*/ jsxs("div", {
                className: styles.textSlot(),
                children: [
                    /*#__PURE__*/ jsx(Field.Error, {
                        match: Boolean(displayErrorText) && !errorMessage,
                        className: styles.errorTextSlot(),
                        "aria-live": "assertive",
                        children: displayErrorText
                    }),
                    !displayErrorText && helperText ? /*#__PURE__*/ jsx(Field.Description, {
                        className: styles.helperTextSlot(),
                        "aria-live": "polite",
                        children: helperText
                    }) : null,
                    maxLength ? /*#__PURE__*/ jsxs("div", {
                        className: styles.characterCount(),
                        children: [
                            "0/",
                            maxLength
                        ]
                    }) : null
                ]
            }) : null
        ]
    });
});
export { InputText_InputText as InputText };
